import {
  BadgeCheckIcon,
  DatabaseIcon,
  HeadsetIcon,
  LandmarkIcon,
  MapPinIcon,
  MonitorSmartphoneIcon,
  ReceiptTextIcon,
  UserPlusIcon,
} from 'lucide-react'
import type { AddOn, ComparisonGroup, Plan, TrustItem } from '../types/pricing'
import type { ProductFaq, ProductTestimonial } from '../types/product'

export const plans: Plan[] = [
  {
    id: 'launch',
    name: 'Launch',
    bestFor: 'Solo merchants and micro-businesses.',
    monthly: 29000,
    annualTotal: 278000,
    annualMonthly: 23000,
    cta: { label: 'Get started', href: '/get-started?plan=launch' },
    features: [
      '1 user',
      'Mobile POS',
      'Sales tracking',
      'RRA EBM invoices and digital receipts',
      'Basic inventory management',
      'Expense tracking',
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    bestFor: 'Small businesses with 2–5 staff and one location.',
    monthly: 59000,
    annualTotal: 566000,
    annualMonthly: 47000,
    cta: { label: 'Get started', href: '/get-started?plan=starter' },
    featuresIntro: 'Everything in Launch, plus:',
    features: [
      'Up to 3 users',
      'Multiple payment methods',
      'Purchases and supplier records',
      'Automated profit and loss reports',
      'Low-stock alerts',
      'Customer balances',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    bestFor: 'Growing businesses with 5–20 staff and multiple branches.',
    monthly: 149000,
    annualTotal: 1430000,
    annualMonthly: 119000,
    cta: { label: 'Get started', href: '/get-started?plan=growth' },
    featuresIntro: 'Everything in Starter, plus:',
    features: [
      'Up to 10 users',
      'Multiple business locations',
      'Payroll and HR tools',
      'Advanced business analytics',
      'Consolidated branch reports',
      'User roles and permissions',
    ],
    popular: true,
  },
  {
    id: 'professional',
    name: 'Professional',
    bestFor: 'Established businesses with 20–100 staff.',
    monthly: 219000,
    annualTotal: 2102000,
    annualMonthly: 175000,
    cta: { label: 'Talk to sales', href: '/contact' },
    featuresIntro: 'Everything in Growth, plus:',
    features: [
      'Unlimited users',
      'API access',
      'Custom business workflows',
      'Custom reports',
      'Advanced permissions',
      '24/7 priority support',
    ],
  },
]

export const trustItems: TrustItem[] = [
  { icon: ReceiptTextIcon, label: 'Clear, transparent pricing' },
  { icon: BadgeCheckIcon, label: 'RRA EBM certified' },
  { icon: HeadsetIcon, label: 'Local support in Kigali' },
  { icon: LandmarkIcon, label: 'Financing subject to eligibility' },
]

const all = { launch: true, starter: true, growth: true, professional: true }
const fromStarter = { launch: false, starter: true, growth: true, professional: true }
const fromGrowth = { launch: false, starter: false, growth: true, professional: true }
const proOnly = { launch: false, starter: false, growth: false, professional: true }

export const comparisonGroups: ComparisonGroup[] = [
  {
    title: 'Point of Sale',
    rows: [
      { label: 'Mobile POS', values: all },
      { label: 'Sales tracking', values: all },
      { label: 'RRA EBM invoices and digital receipts', values: all },
      { label: 'Discounts and returns', values: all },
      { label: 'Multiple payment methods', values: fromStarter },
    ],
  },
  {
    title: 'Inventory',
    rows: [
      { label: 'Basic inventory management', values: all },
      { label: 'Low-stock alerts', values: fromStarter },
      { label: 'Stock transfers between locations', values: fromGrowth },
    ],
  },
  {
    title: 'Purchases',
    rows: [
      { label: 'Purchase orders', values: fromStarter },
      { label: 'Supplier records and balances', values: fromStarter },
    ],
  },
  {
    title: 'Expenses',
    rows: [
      { label: 'Expense tracking', values: all },
      { label: 'Receipt photos', values: all },
      { label: 'Recurring bills and reminders', values: fromStarter },
    ],
  },
  {
    title: 'Customers',
    rows: [
      { label: 'Customer profiles', values: all },
      { label: 'Customer balances and credit', values: fromStarter },
    ],
  },
  {
    title: 'Reports & analytics',
    rows: [
      { label: 'Daily sales summary', values: all },
      { label: 'Automated profit and loss reports', values: fromStarter },
      { label: 'Advanced business analytics', values: fromGrowth },
      { label: 'Consolidated branch reports', values: fromGrowth },
      { label: 'Custom reports', values: proOnly },
    ],
  },
  {
    title: 'Payroll & HR',
    rows: [
      { label: 'Payroll', values: fromGrowth },
      { label: 'Staff records and HR tools', values: fromGrowth },
    ],
  },
  {
    title: 'Users & locations',
    rows: [
      {
        label: 'Users',
        values: { launch: '1 user', starter: 'Up to 3 users', growth: 'Up to 10 users', professional: 'Unlimited' },
      },
      {
        label: 'Business locations',
        values: { launch: '1', starter: '1', growth: 'Multiple', professional: 'Multiple' },
      },
      {
        label: 'Roles and permissions',
        values: { launch: false, starter: false, growth: 'Standard', professional: 'Advanced' },
      },
    ],
  },
  {
    title: 'API & workflows',
    rows: [
      { label: 'API access', values: proOnly },
      { label: 'Custom business workflows', values: proOnly },
    ],
  },
  {
    title: 'Support',
    rows: [
      { label: 'Help centre and email support', values: all },
      {
        label: 'Phone and chat support',
        values: { launch: 'Business hours', starter: 'Business hours', growth: 'Business hours', professional: '24/7 priority' },
      },
      {
        label: 'Onboarding',
        values: { launch: 'Self-serve', starter: 'Self-serve', growth: 'Guided', professional: 'Dedicated' },
      },
    ],
  },
]

export const addOns: AddOn[] = [
  { icon: MapPinIcon, title: 'Extra location', copy: 'Add another shop, branch, or warehouse to your plan.' },
  { icon: UserPlusIcon, title: 'Additional users', copy: 'Give more staff their own login beyond your plan’s limit.' },
  { icon: MonitorSmartphoneIcon, title: 'POS hardware', copy: 'Receipt printers, scanners, cash drawers, and tablets.' },
  { icon: DatabaseIcon, title: 'Onboarding and data migration', copy: 'Our Kigali team moves your products, customers, and history.' },
]

export const pricingTestimonial: ProductTestimonial = {
  quote: 'With Kayko, managing my inventory and tracking my sales has become second nature.',
  name: 'Jemima Nyirasafari',
  role: 'Owner',
  business: 'All-in-one supermarket',
  location: 'Kicukiro, Kigali',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/26e15333-45ec-4421-ac04-24cf98518008.jpg',
}

export const pricingFaq: ProductFaq[] = [
  {
    question: 'How do I change plans?',
    answer:
      'You can upgrade or downgrade at any time from your account settings. Upgrades take effect immediately and are charged pro rata; downgrades apply from your next billing date.',
  },
  {
    question: 'How does annual billing work?',
    answer:
      'With annual billing you pay for 12 months upfront and save 20% compared with paying monthly. Your plan renews each year, and we remind you before renewal.',
  },
  {
    question: 'What counts as a user and a location?',
    answer:
      'A user is anyone with their own Kayko login, such as a cashier, manager, or accountant. A location is a shop, branch, or warehouse with its own stock and sales.',
  },
  {
    question: 'Are there payment provider fees?',
    answer:
      'Kayko does not add a transaction fee. Mobile money, card, and bank-transfer providers may charge their own fees, which vary by payment method.',
  },
  {
    question: 'Do I need special hardware?',
    answer:
      'No. Kayko works on the phone, tablet, or computer you already have. Receipt printers, scanners, and cash drawers are optional and available as an add-on.',
  },
  {
    question: 'Can I export my data?',
    answer:
      'Yes. Your data belongs to you. Export sales, stock, customers, and reports to spreadsheet or PDF at any time, on every plan.',
  },
  {
    question: 'Who can apply for Business Financing?',
    answer:
      'Registered Kayko businesses with at least 90 days of active business records can apply. Financing is subject to eligibility and approval by our licensed lending partner.',
  },
  {
    question: 'How do I get support?',
    answer:
      'Every plan includes the help centre and email support from our team in Kigali. Phone and chat support is available during business hours, and 24/7 on Professional.',
  },
]
